import { ReactElement, useState } from 'react'
import { ApolloError, useApolloClient, useMutation } from '@apollo/client'
import { Formik, FormikErrors } from 'formik'
import { useIntl, FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty, max, findIndex } from 'lodash'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Timer } from 'components/Timer'
import FormLayout from 'features/App/components/FormLayout'
import { BookingPaymentsList } from 'features/Bookings/components/BookingPaymentsList'
import { BookingTripsDetails } from 'features/Bookings/components/BookingTripsDetails'
import { EditTrip } from 'features/Bookings/components/EditTrip'
import { passengerTypeMessages } from 'features/Bookings/components/PassengerType/messages'
import { CANCEL_BOOKING_TRIP_CHANGE_PROPOSAL } from 'features/Bookings/mutations-legacy'
import { getPassengersByTickets, isTicketCancelled } from 'resolvers/bookings'
import { resolvers as loginResolvers } from 'resolvers/login'
import { getUnprocessableErrors } from 'utils/error'
import { CouponErrorNotification } from './CouponErrorNotification'
import { TimerIsUpModal } from './TimerIsUpModal'
import { schema } from './schema'
import { IEditBookingFormProps, IEditBookingValues, IEditBookingProps, IAncillarieLuggageTicket } from './types'
import { IMovedBooking, IMovedBookingTicket, IMovedBookingTrip, TPassengerWithTickets } from 'features/Bookings/types'
import { GTBookingV2Coupon, GTBookingV2TicketAncillaryType } from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'
import AncillariesForm from 'features/Bookings/components/EditBooking/AncillariesForm'
import { getShortId } from 'utils/string'

const TimerWrapper = styled(FormLayout.CardContent)`
  padding: 0;
  position: relative;
`

const TimerContainer = styled.div<TSFixMe>`
  position: absolute;
  right: 0;
  top: 40px;
`

const getSeatsByPassenger = ({
  passengers,
  passenger,
  booking
}: {
  passengers: TPassengerWithTickets[]
  passenger: TPassengerWithTickets
  booking: IEditBookingValues
}): string => {
  let maxTickets = 0
  passengers.forEach((passenger): TSFixMe => {
    maxTickets = max([maxTickets, passenger.tickets.length]) as number
  })
  const allTrips = [...booking.departureTrips, ...booking.returnTrips]

  return allTrips.reduce((result, trip, index): TSFixMe => {
    const tripTicket = passenger.tickets.find((ticket): TSFixMe => {
      return ticket?.trip?.id === trip.id
    })

    if (tripTicket) {
      if (!result) {
        return tripTicket?.seat?.label as string
      } else {
        return `${result} | ${tripTicket?.seat?.label}`
      }
    } else if (index < maxTickets) {
      if (!result) {
        return '-'
      } else {
        return `${result} | -`
      }
    }

    return result
  }, '')
}

const findOldTrip = ({
  oldDepartureTrips,
  oldReturnTrips,
  newDepartureTrips,
  newReturnTrips,
  newTrip
}: {
  oldDepartureTrips: IMovedBookingTrip[]
  oldReturnTrips: IMovedBookingTrip[]
  newDepartureTrips: IMovedBookingTrip[]
  newReturnTrips: IMovedBookingTrip[]
  newTrip: IMovedBookingTrip
}): IMovedBookingTrip => {
  let tripIndex = findIndex(newDepartureTrips, (departureTrip): TSFixMe => {
    return departureTrip.id === newTrip.id && departureTrip.segment.id === newTrip.segment.id
  })

  if (tripIndex > -1) {
    return oldDepartureTrips[tripIndex]
  }

  tripIndex = findIndex(newReturnTrips, (returnTrip): TSFixMe => {
    return returnTrip.id === newTrip.id && returnTrip.segment.id === newTrip.segment.id
  })

  if (tripIndex > -1) {
    return oldReturnTrips[tripIndex]
  }

  return newTrip
}

const isCouponApplied = (coupon: GTBookingV2Coupon | null | undefined): boolean => {
  return !isEmpty(coupon)
}

const EditBookingForm = (props: IEditBookingFormProps): ReactElement => {
  let content
  const {
    dirty: isDirty,
    handleSubmit,
    changeAncillariesMutation,
    changeBuyerMutation,
    changePassengerMutation,
    changePaymentMutation,
    confirmBookingTripChangesMutation,
    values,
    handleChange,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    booking,
    editPassengersDisabled,
    editBuyerDisabled,
    editPriceDisabled,
    canEditSegment,
    showExtraServices,
    isMoveBooking
  } = props
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const [tripToEdit, setTripToEdit] = useState<IMovedBookingTrip | undefined>()
  const [isTimerEnded, setIsTimerEnded] = useState(false)
  const [isStartingToReserveSeat, setIsStartingToReserveSeat] = useState(false)
  const [couponError, setCouponError] = useState<ApolloError | string | undefined>()
  const [isShowingCouponError, showCouponError] = useState(false)
  const [cancelBookingTripChangeProposal] = useMutation(CANCEL_BOOKING_TRIP_CHANGE_PROPOSAL)

  const { buyer, passengers } = values

  const handleEditTrip = (trip: IMovedBookingTrip): void => {
    setTripToEdit(trip)
  }

  const handleAbortEditTrip = (): void => {
    setTripToEdit(undefined)
  }

  const handleTimeIsUp = (): void => {
    if (!isTimerEnded) {
      setIsTimerEnded(true)
      setIsStartingToReserveSeat(false)

      setTripToEdit(undefined)
    }
  }

  const handleStartToReserveSeat = (): void => {
    if (isStartingToReserveSeat) {
      setIsStartingToReserveSeat(false)
    }

    setIsStartingToReserveSeat(true)
  }

  const handleCancelReservation = (): void => {
    setIsStartingToReserveSeat(false)
  }

  const handleCouponError = (error: ApolloError | string): void => {
    setCouponError(error)
    showCouponError(true)
  }

  const handleCouponSuccess = (): void => {
    setCouponError(undefined)
    showCouponError(false)
  }

  const handleBookingTripChangeProposalCreated = ({
    booking: newBooking,
    tripChangeProposalId,
    modifiedTripId
  }: {
    booking: IMovedBooking
    tripChangeProposalId: string
    modifiedTripId: string
  }): void => {
    if (isCouponApplied(booking.coupon) && !isCouponApplied(newBooking.coupon)) {
      handleCouponError('error')
    } else {
      handleCouponSuccess()
    }
    setFieldValue('tickets', newBooking.tickets)
    setFieldValue('passengers', getPassengersByTickets({ tickets: newBooking.tickets }))
    setFieldValue('departureTrips', newBooking.departureTrips)
    setFieldValue('returnTrips', newBooking.returnTrips)
    setFieldValue('coupon', newBooking.coupon)
    setFieldValue('tripChangeProposalId', tripChangeProposalId)
    setFieldValue('modifiedTripId', modifiedTripId)

    setTripToEdit(undefined)
  }

  if (!isEmpty(tripToEdit)) {
    const oldTrip = findOldTrip({
      oldDepartureTrips: booking.departureTrips,
      oldReturnTrips: booking.returnTrips,
      newDepartureTrips: values.departureTrips,
      newReturnTrips: values.returnTrips,
      newTrip: tripToEdit
    })
    const oldTripTickets = booking.tickets
      .filter((ticket) => {
        return ticket?.trip?.id === oldTrip.id && !isTicketCancelled(ticket)
      })
      .map((ticket) => {
        const returnTrip = booking.returnTrips.find((trip) => trip.id === ticket?.trip?.id)
        const payments = booking.payments.filter((payment) => {
          return payment.tickets.find(({ id }) => id === ticket.id)
        })

        return {
          ...ticket,
          payments,
          isReturn: !isEmpty(returnTrip)
        }
      })

    content = (
      <EditTrip
        booking={booking}
        oldTickets={oldTripTickets}
        trip={oldTrip}
        handleAbortEditTrip={handleAbortEditTrip}
        onStartToReserveSeat={handleStartToReserveSeat}
        onCancelReservation={handleCancelReservation}
        onBookingTripChangeProposalCreated={handleBookingTripChangeProposalCreated}
        editPriceDisabled={editPriceDisabled}
        canEditSegment={canEditSegment}
      />
    )
  } else {
    const isProcessingMutation =
      changeAncillariesMutation?.loading ||
      changeBuyerMutation?.loading ||
      changePassengerMutation?.loading ||
      changePaymentMutation?.loading ||
      confirmBookingTripChangesMutation?.loading
    const handleCancel = (): void => {
      if (values.tripChangeProposalId) {
        cancelBookingTripChangeProposal({
          variables: {
            bookingId: booking.id,
            tripChangeProposalId: values.tripChangeProposalId
          }
        })
      }
    }
    content = (
      <form autoComplete="off" onSubmit={handleSubmit}>
        <FormLayout.CardContent>
          <Row>
            <Column>
              <Heading.H5>
                <FormattedMessage id="bookings.form.trips.title" defaultMessage="Trip details" />
              </Heading.H5>
            </Column>
          </Row>
          <Row>
            <Column>
              <BookingTripsDetails
                departureTrips={values.departureTrips}
                returnTrips={values.returnTrips}
                tickets={values.tickets}
                onEditTrip={handleEditTrip}
                tripChangeProposalId={values.tripChangeProposalId}
                modifiedTripId={values.modifiedTripId}
                isMoveBooking={isMoveBooking}
              />
            </Column>
          </Row>

          <Row>
            <Column>
              <Heading.H5>
                <FormattedMessage id="bookings.form.passengers.title" defaultMessage="Passengers details" />
              </Heading.H5>
            </Column>
          </Row>

          {passengers.map((passenger, index): TSFixMe => {
            return (
              <Row key={index}>
                <Column width={`${(100 * 10) / (12 * 3)}%`}>
                  <Input
                    id={`passengers[${index}].firstName`}
                    label={<FormattedMessage id="dictionary.firstName" defaultMessage="First name" />}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={passenger.firstName}
                    touched={touched?.passengers?.[index]?.firstName}
                    errors={(errors?.passengers?.[index] as FormikErrors<TPassengerWithTickets>)?.firstName}
                    disabled={editPassengersDisabled}
                  />
                </Column>

                <Column width={`${(100 * 10) / (12 * 3)}%`}>
                  <Input
                    id={`passengers[${index}].lastName`}
                    label={<FormattedMessage id="dictionary.lastName" defaultMessage="Last name" />}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={passenger.lastName}
                    touched={touched?.passengers?.[index]?.lastName}
                    errors={(errors?.passengers?.[index] as FormikErrors<TPassengerWithTickets>)?.lastName}
                    disabled={editPassengersDisabled}
                  />
                </Column>

                <Column width={`${(100 * 10) / (12 * 3)}%`}>
                  <Input
                    id={`passengers[${index}].typeV2.name`}
                    label={<FormattedMessage id="bookings.passengers.ticketType" defaultMessage="Ticket type" />}
                    value={safeFormatMessage({ intl, messages: passengerTypeMessages, key: passenger.type?.name })}
                    disabled
                    readOnly
                  />
                </Column>

                <Column width={`${(100 * 6) / (12 * 3)}%`}>
                  <Input
                    id={`passengers[${index}].seats`}
                    label={<FormattedMessage id="bookings.passengers.seats" defaultMessage="Seats" />}
                    value={getSeatsByPassenger({ passengers, passenger, booking: values })}
                    disabled
                    readOnly
                  />
                </Column>
              </Row>
            )
          })}
          {showExtraServices ? (
            <>
              <Row>
                <Column>
                  <Heading.H5>
                    <FormattedMessage id="bookings.form.ancillariesSection.title" defaultMessage="Extra Services" />
                  </Heading.H5>
                </Column>
              </Row>
              <AncillariesForm />
            </>
          ) : null}
          <Row>
            <Column>
              <Heading.H5>
                <FormattedMessage id="bookings.form.buyer.title" defaultMessage="Buyer details" />
              </Heading.H5>
            </Column>
          </Row>

          <Row>
            <Column width={`${(100 * 10) / (12 * 3)}%`}>
              <Input
                id="buyer.name"
                label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
                value={`${buyer.firstName} ${buyer.lastName}`}
                disabled
                readOnly
              />
            </Column>

            <Column width={`${(100 * 10) / (12 * 3)}%`}>
              <Input
                id="buyer.email"
                type="email"
                label={<FormattedMessage id="dictionary.email" defaultMessage="Email" />}
                onChange={handleChange}
                onBlur={handleBlur}
                value={buyer.email}
                touched={touched.buyer && touched.buyer.email}
                errors={errors.buyer && errors.buyer.email}
                disabled={editBuyerDisabled}
              />
            </Column>

            <Column width={`${(100 * 10) / (12 * 3)}%`}>
              <Input
                id="buyer.phone"
                type="phone"
                label={<FormattedMessage id="dictionary.phone" defaultMessage="Phone" />}
                onChange={handleChange}
                onBlur={handleBlur}
                value={buyer.phone}
                touched={touched.buyer && touched.buyer.phone}
                errors={errors.buyer && errors.buyer.phone}
                disabled={editBuyerDisabled}
              />
            </Column>
          </Row>

          <Row>
            <Column>
              <Heading.H5>
                <FormattedMessage id="bookings.form.payment.title" defaultMessage="Payment details" />
              </Heading.H5>
            </Column>
          </Row>

          <Row>
            <Column width={`${(100 * 10) / (12 * 3)}%`}>
              <Input
                id="coupon.code"
                label={<FormattedMessage id="dictionary.coupon" defaultMessage="Coupon" />}
                value={values.coupon?.code || ''}
                disabled
                readOnly
                touched
                errors={
                  couponError && (
                    <FormattedMessage id="bookings.form.coupon.notElegible" defaultMessage="(Not Elegible)" />
                  )
                }
              />
            </Column>
          </Row>

          <Row>
            <Column>
              <BookingPaymentsList
                newTickets={values.tickets}
                departureTrips={values.departureTrips}
                returnTrips={values.returnTrips}
                tripChangeProposalId={values.tripChangeProposalId}
                {...props}
              />
            </Column>
          </Row>
        </FormLayout.CardContent>

        <FormLayout.Split />

        <FormLayout.CardFooter>
          {changeAncillariesMutation?.error && (
            <div>
              <ErrorMessage errors={getUnprocessableErrors(changeAncillariesMutation.error)}>
                <FormattedMessage
                  id="bookings.ancillaries.error"
                  defaultMessage="There was an error while editing ancillaries"
                />
              </ErrorMessage>
            </div>
          )}

          {changeBuyerMutation?.error && (
            <div>
              <ErrorMessage errors={getUnprocessableErrors(changeBuyerMutation.error)}>
                <FormattedMessage
                  id="bookings.buyer.error"
                  defaultMessage="There was an error while editing the buyer"
                />
              </ErrorMessage>
            </div>
          )}

          {changePassengerMutation?.error && (
            <div>
              <ErrorMessage errors={getUnprocessableErrors(changePassengerMutation.error)}>
                <FormattedMessage
                  id="bookings.passengers.error"
                  defaultMessage="There was an error while editing the passengers"
                />
              </ErrorMessage>
            </div>
          )}

          {changePaymentMutation?.error && (
            <div>
              <ErrorMessage errors={getUnprocessableErrors(changePaymentMutation.error)}>
                <FormattedMessage
                  id="bookings.payments.error"
                  defaultMessage="There was an error while editing the payments"
                />
              </ErrorMessage>
            </div>
          )}

          {confirmBookingTripChangesMutation?.error && (
            <div>
              <ErrorMessage errors={getUnprocessableErrors(confirmBookingTripChangesMutation?.error)}>
                <FormattedMessage
                  id="bookings.editTrip.error"
                  defaultMessage="There was an error while editing your trips"
                />
              </ErrorMessage>
            </div>
          )}

          <FormLayout.CardFooterActions>
            <Button
              as={NavLink}
              to={`/bookings?reservations=${getShortId(booking.id)}`}
              kind="cancel"
              onClick={handleCancel}
              disabled={isProcessingMutation}
            >
              <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
            </Button>

            <Button type="submit" kind="primary" loading={isProcessingMutation} disabled={!isDirty}>
              <FormattedMessage id="dictionary.confirm" defaultMessage="Confirm" />
            </Button>
          </FormLayout.CardFooterActions>
        </FormLayout.CardFooter>

        {isTimerEnded && <TimerIsUpModal />}
      </form>
    )
  }

  return (
    <>
      {isStartingToReserveSeat && (
        <TimerWrapper>
          <TimerContainer>
            <Timer duration={10 * 60} onTimeIsUp={handleTimeIsUp} />
          </TimerContainer>
        </TimerWrapper>
      )}

      {content}

      {isShowingCouponError && (
        <CouponErrorNotification
          onClose={(): void => {
            showCouponError(false)
          }}
        />
      )}
    </>
  )
}
export const mapTicketForForm = (ticket: IMovedBookingTicket): IAncillarieLuggageTicket => ({
  ...ticket,
  luggageAncillaries:
    ticket.ancillaries
      ?.filter(({ type }) =>
        (
          [
            GTBookingV2TicketAncillaryType.Luggage,
            GTBookingV2TicketAncillaryType.CarryOn
          ] as GTBookingV2TicketAncillaryType[]
        ).includes(type)
      )
      .map((ancillary) => {
        return {
          ancillaryId: ancillary.id as string,
          count: ancillary.quantity,
          price: ancillary.total,
          type: ancillary.type
        }
      }) || []
})

export const mapBookingToForm = (booking: IMovedBooking): IEditBookingValues => {
  const passengers = getPassengersByTickets(booking)
  const tickets =
    booking.tickets
      .filter((ticket): boolean => {
        return !isTicketCancelled(ticket)
      })
      .map((ticket) => {
        const returnTrip = booking.returnTrips.find((trip): TSFixMe => trip.id === ticket?.trip?.id)

        return {
          ...ticket,
          isReturn: !isEmpty(returnTrip)
        }
      }) || []
  const departureTickets = tickets.filter(({ isReturn }) => !isReturn)
  const returnTickets = tickets.filter(({ isReturn }) => isReturn)

  return {
    id: booking?.id || '',
    buyer: {
      firstName: booking?.buyer?.firstName || '',
      lastName: booking?.buyer?.lastName || '',
      email: booking?.buyer?.email || '',
      phone: booking?.buyer?.phone || ''
    },
    passengers,
    departureTrips: booking.departureTrips,
    returnTrips: booking.returnTrips,
    payments: booking.payments || [],
    tickets,
    seatChanges: [],
    coupon: booking.coupon,
    tripChangeProposalId: '',
    modifiedTripId: '',
    departureTicketsWithLuggage: departureTickets.map(mapTicketForForm),
    returnTicketsWithLuggage: returnTickets.map(mapTicketForForm)
  }
}

export const EditBooking = ({
  booking,
  onSubmit,
  changeBuyerMutation,
  changePassengerMutation,
  changePaymentMutation,
  changeAncillariesMutation,
  confirmBookingTripChangesMutation,
  editPassengersDisabled,
  editBuyerDisabled,
  editPriceDisabled,
  canEditSegment,
  isMoveBooking
}: IEditBookingProps): ReactElement => {
  const intl = useIntl()
  const initialBookingValues = booking.toFormValues()
  const departureTickets = initialBookingValues.tickets.filter(({ isReturn }) => !isReturn)
  const returnTickets = initialBookingValues.tickets.filter(({ isReturn }) => isReturn)
  const hasDepartureLuggage = departureTickets.some(({ ancillaries }) => Number(ancillaries?.length) > 0)
  const hasReturnLuggage = returnTickets.some(({ ancillaries }) => Number(ancillaries?.length) > 0)

  const isPhoneRequired = !!booking?.buyer?.phone
  const isEmailRequired = !!booking?.buyer?.email

  return (
    <Formik
      initialValues={initialBookingValues}
      validationSchema={schema(intl, {
        isPhoneRequired,
        isEmailRequired,
        isPassengerRequired: !editPassengersDisabled
      })}
      onSubmit={onSubmit}
    >
      {(props): ReactElement => {
        return (
          <EditBookingForm
            {...props}
            booking={booking as TSFixMe}
            changeBuyerMutation={changeBuyerMutation}
            changePassengerMutation={changePassengerMutation}
            changePaymentMutation={changePaymentMutation}
            changeAncillariesMutation={changeAncillariesMutation}
            confirmBookingTripChangesMutation={confirmBookingTripChangesMutation}
            editPassengersDisabled={editPassengersDisabled}
            editBuyerDisabled={editBuyerDisabled}
            editPriceDisabled={editPriceDisabled}
            canEditSegment={canEditSegment}
            showExtraServices={hasDepartureLuggage || hasReturnLuggage}
            isMoveBooking={isMoveBooking}
          />
        )
      }}
    </Formik>
  )
}
